import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { 
  FaRunning, 
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUp,
  FaChevronLeft,
  FaChevronRight,
  FaFlag
} from 'react-icons/fa';
import { getActivityDetails, ActivityDetail, getTeamsByTournament, getTournaments, Team, Tournament } from '../../services/activitiesService';
import DatePickerCustom from '../../components/DatePickerCustom';
import './activities.css';

interface FormValues {
  tournamentId: string;
  teamId: string;
  startDate: string;
  endDate: string;
}

const Activities: React.FC = () => {
  // States cho dữ liệu và phân trang
  const [activities, setActivities] = useState<ActivityDetail[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  // States cho các thông số lọc
  const [teams, setTeams] = useState<Team[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [filters, setFilters] = useState(() => {
    const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
    
    const startDateStr = firstDayOfMonth.toISOString().split('T')[0];
    const endDateStr = lastDayOfMonth.toISOString().split('T')[0];
    
    return {
      teamId: null as number | null,
      tournamentId: null as number | null,
      startDateLocal: `${startDateStr}T00:00:00`,
      endDateLocal: `${endDateStr}T23:59:59`,
      page: 0,
      size: 10,
      sortBy: 'startDateLocal',
      sortDir: 'desc'
    };
  });

  // Khởi tạo react-hook-form
  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: (() => {
      const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      const lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
      
      const startDateStr = firstDayOfMonth.toISOString().split('T')[0];
      const endDateStr = lastDayOfMonth.toISOString().split('T')[0];
      
      return {
        tournamentId: '',
        teamId: '',
        startDate: startDateStr,
        endDate: endDateStr
      };
    })()
  });

  // Theo dõi thay đổi giá trị tournamentId để cập nhật danh sách teams
  const watchTournamentId = watch('tournamentId');

  // Lấy danh sách giải đấu và đội khi component được mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const tournamentList = await getTournaments();
        setTournaments(tournamentList);
        
        // Nếu có tournamentId đang được chọn, lấy danh sách đội
        if (filters.tournamentId) {
          const teamList = await getTeamsByTournament(filters.tournamentId);
          setTeams(teamList);
        }
      } catch (err: any) {
        console.error('Lỗi khi lấy dữ liệu ban đầu:', err);
        setError(err.message || 'Không thể lấy dữ liệu ban đầu');
      }
    };

    fetchInitialData();
  }, []);

  // Lấy danh sách hoạt động khi filters thay đổi
  useEffect(() => {
    fetchActivities();
  }, [filters]);

  // Cập nhật danh sách đội khi thay đổi tournamentId
  useEffect(() => {
    const updateTeams = async () => {
      if (watchTournamentId) {
        try {
          const tournamentId = parseInt(watchTournamentId);
        const teamList = await getTeamsByTournament(tournamentId);
        setTeams(teamList);
          setValue('teamId', ''); // Reset teamId khi đổi tournament
        } catch (err: any) {
          console.error('Lỗi khi lấy danh sách đội:', err);
          setError(err.message || 'Không thể lấy danh sách đội');
        }
      } else {
        setTeams([]);
      }
    };

    updateTeams();
  }, [watchTournamentId, setValue]);

  // Xử lý khi thay đổi sắp xếp
  const handleSortChange = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortDir: prev.sortBy === field && prev.sortDir === 'desc' ? 'asc' : 'desc',
      page: 0
    }));
  };

  // Xử lý khi thay đổi trang
  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Xử lý khi submit form
  const onSubmit = (data: FormValues) => {
    // Chuyển đổi chuỗi ngày thành đối tượng Date rồi định dạng lại ISO string để đảm bảo đúng định dạng
    // Tạo đối tượng Date với giờ là 12:00 để tránh vấn đề múi giờ
    const startDate = new Date(data.startDate);
    startDate.setHours(0, 0, 0, 0);
    const startDateIso = startDate.toISOString().split('T')[0];
    
    const endDate = new Date(data.endDate);
    endDate.setHours(23, 59, 59, 999);
    const endDateIso = endDate.toISOString().split('T')[0];
    
    setFilters(prev => ({
      ...prev,
      tournamentId: data.tournamentId ? parseInt(data.tournamentId) : null,
      teamId: data.teamId ? parseInt(data.teamId) : null,
      startDateLocal: `${startDateIso}T00:00:00`,
      endDateLocal: `${endDateIso}T23:59:59`,
      page: 0
    }));
  };

  // Lấy danh sách hoạt động từ API
  const fetchActivities = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getActivityDetails(filters);
      
      if (response.result === 'SUCCESS') {
        setActivities(response.data.content);
        setTotalElements(response.data.totalElements);
        setTotalPages(response.data.totalPages);
      } else {
        setError(response.message || 'Có lỗi xảy ra khi lấy dữ liệu');
      }
    } catch (err: any) {
      console.error('Lỗi khi lấy danh sách hoạt động:', err);
      setError(err.message || 'Không thể lấy danh sách hoạt động');
    } finally {
      setLoading(false);
    }
  };

  // Hiển thị danh sách hoạt động
  const renderActivities = () => {
    if (loading) {
    return (
        <div className="activity-loading">
          <div className="spinner"></div>
          <p>Đang tải dữ liệu...</p>
      </div>
    );
    }

    if (error) {
    return (
        <div className="activity-error">
          <p className="error-message">{error}</p>
          <button className="retry-button" onClick={fetchActivities}>
            Thử lại
          </button>
      </div>
    );
    }

    if (activities.length === 0) {
      return (
        <div className="activity-empty">
          <p>Không có hoạt động nào được tìm thấy</p>
        </div>
      );
    }
    
    return (
      <table className="activity-table">
        <thead>
          <tr>
            <th>
              <div className="sort-header" onClick={() => handleSortChange('startDateLocal')}>
                <span>Ngày</span>
                {filters.sortBy === 'startDateLocal' && (
                  filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                )}
            </div>
            </th>
            <th>Người dùng</th>
            <th>
              <div className="sort-header" onClick={() => handleSortChange('name')}>
                <span>Tên hoạt động</span>
                {filters.sortBy === 'name' && (
                  filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                )}
              </div>
            </th>
            <th>Loại</th>
            <th>
              <div className="sort-header" onClick={() => handleSortChange('distance')}>
                <span>Khoảng cách</span>
                {filters.sortBy === 'distance' && (
                  filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                )}
              </div>
            </th>
            <th>
              <div className="sort-header" onClick={() => handleSortChange('movingTime')}>
                <span>Thời gian di chuyển</span>
                {filters.sortBy === 'movingTime' && (
                  filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                )}
              </div>
            </th>
            <th>
              <div className="sort-header" onClick={() => handleSortChange('averageSpeed')}>
                <span>Tốc độ TB</span>
                {filters.sortBy === 'averageSpeed' && (
                  filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                )}
              </div>
            </th>
            <th>
              <div className="sort-header" onClick={() => handleSortChange('totalElevationGain')}>
                <span>Độ cao</span>
                {filters.sortBy === 'totalElevationGain' && (
                  filters.sortDir === 'desc' ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />
                )}
              </div>
            </th>
              </tr>
            </thead>
            <tbody>
          {activities.map((activity) => (
            <tr key={activity.id}>
              <td>{formatDate(activity.startDateLocal)}</td>
              <td>
                <div className="user-info">
                  <img 
                    src={activity.userProfileImageUrl || '/assets/default-avatar.png'} 
                    alt={`${activity.userFirstName} ${activity.userLastName}`}
                    className="user-avatar"
                  />
                  <span>{activity.userFirstName} {activity.userLastName}</span>
                </div>
              </td>
              <td>
                {activity.name} {activity.suspiciousActivity === 1 && <FaFlag className="suspicious-flag" color="red" title="Hoạt động đáng nghi ngờ" />}
              </td>
              <td>
                <span className={`badge badge-${getActivityTypeColor(activity.type)}`}>
                  {activity.type}
                </span>
                  </td>
              <td>{formatDistance(activity.distance)}</td>
              <td>{formatTime(activity.movingTime)}</td>
              <td>{formatSpeed(activity.averageSpeed)}</td>
              <td>{formatElevation(activity.totalElevationGain)}</td>
                </tr>
              ))}
            </tbody>
          </table>
    );
  };

  // Hiển thị điều khiển phân trang
  const renderPagination = () => {
    return (
      <div className="pagination">
        <div className="pagination-info">
          Hiển thị {activities.length} trong tổng số {totalElements} hoạt động
        </div>
        <div className="pagination-controls">
          <button 
            className="pagination-btn" 
            disabled={filters.page === 0}
            onClick={() => handlePageChange(filters.page - 1)}
          >
            <FaChevronLeft />
          </button>
          <span className="pagination-text">
            Trang {filters.page + 1} / {totalPages}
          </span>
          <button 
            className="pagination-btn" 
            disabled={filters.page >= totalPages - 1}
            onClick={() => handlePageChange(filters.page + 1)}
          >
            <FaChevronRight />
          </button>
        </div>
      </div>
    );
  };

  // Hàm hỗ trợ định dạng
  const formatDate = (dateArray: number[]) => {
    if (!dateArray || dateArray.length < 3) return 'N/A';
    const [year, month, day, hour, minute] = dateArray;
    return `${day}/${month}/${year} ${hour !== undefined && minute !== undefined ? `${hour}:${minute < 10 ? '0' + minute : minute}` : ''}`;
  };

  const formatDistance = (distance: number) => {
    if (distance < 1000) {
      return `${distance.toFixed(0)} m`;
    }
    return `${(distance / 1000).toFixed(2)} km`;
  };

  const formatTime = (seconds: number) => {
    if (!seconds) return 'N/A';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const formatSpeed = (speed: number) => {
    if (!speed) return 'N/A';
    
    // Chuyển đổi m/s sang km/h
    const speedKmh = speed * 3.6;
    
    if (speedKmh < 10) {
      // Làm tròn 2 chữ số thập phân cho tốc độ nhỏ
      return `${speedKmh.toFixed(2)} km/h`;
    } else {
      // Làm tròn 1 chữ số thập phân cho tốc độ lớn
      return `${speedKmh.toFixed(1)} km/h`;
    }
  };

  const formatElevation = (elevation: number) => {
    if (!elevation) return '0 m';
    
    if (elevation < 1000) {
      return `${elevation.toFixed(0)} m`;
    }
    return `${(elevation / 1000).toFixed(2)} km`;
  };

  const getActivityTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'run':
        return 'green';
      case 'ride':
        return 'blue';
      case 'swim':
        return 'cyan';
      case 'workout':
        return 'orange';
      case 'walk':
        return 'purple';
      default:
        return 'gray';
    }
  };

  return (
    <div className="activities-container">
      <h1 className="activities-title">Thống Kê Hoạt Động</h1>

      {/* Bộ lọc mới */}
      <div className="filter-container">
        <div className="filter-header">
          <h3 className="filter-title"><FaFilter /> Bộ lọc</h3>
        </div>
        <div className="filter-content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="filter-row">
              <div className="filter-item">
                <label htmlFor="tournamentId">Giải đấu</label>
                <Controller
                  name="tournamentId"
                  control={control}
                  render={({ field }) => (
          <select 
                      id="tournamentId"
                      {...field}
            className="filter-select"
          >
                      <option value="">Tất cả giải đấu</option>
            {tournaments.map(tournament => (
                        <option key={tournament.id} value={tournament.id.toString()}>
                {tournament.name}
              </option>
            ))}
          </select>
                  )}
                />
        </div>
        
              <div className="filter-item">
                <label htmlFor="teamId">Đội</label>
                <Controller
                  name="teamId"
                  control={control}
                  render={({ field }) => (
            <select 
                      id="teamId"
                      {...field}
              className="filter-select"
                      disabled={!watchTournamentId}
                    >
                      <option value="">Tất cả đội</option>
              {teams.map(team => (
                        <option key={team.id} value={team.id.toString()}>
                  {team.name}
                </option>
              ))}
            </select>
                  )}
                />
              </div>
          </div>
            
            <div className="filter-row">
              <div className="filter-item">
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePickerCustom
                      label="Từ ngày"
                      value={field.value}
                      onChange={(date) => {
                        if (date) {
                          // Chuyển đổi thành chuỗi ISO YYYY-MM-DD
                          const dateStr = date.toISOString().split('T')[0];
                          field.onChange(dateStr);
                        } else {
                          field.onChange('');
                        }
                      }}
                      className="filter-input activity-datepicker"
                    />
                  )}
                />
              </div>
              
              <div className="filter-item">
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <DatePickerCustom
                      label="Đến ngày"
                      value={field.value}
                      onChange={(date) => {
                        if (date) {
                          // Chuyển đổi thành chuỗi ISO YYYY-MM-DD
                          const dateStr = date.toISOString().split('T')[0];
                          field.onChange(dateStr);
                        } else {
                          field.onChange('');
                        }
                      }}
                      className="filter-input activity-datepicker"
                    />
                  )}
                />
          </div>
          
              <div className="filter-actions">
                <button type="submit" className="filter-button apply-button">
                  <span>Áp dụng</span>
                </button>
                <button 
                  type="button" 
                  className="filter-button reset-button"
                  onClick={() => {
                    setValue('tournamentId', '');
                    setValue('teamId', '');
                    
                    const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                    const lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
                    
                    const startDateStr = firstDayOfMonth.toISOString().split('T')[0];
                    const endDateStr = lastDayOfMonth.toISOString().split('T')[0];
                    
                    setValue('startDate', startDateStr);
                    setValue('endDate', endDateStr);
                    
                    setFilters(prev => ({
                      ...prev,
                      tournamentId: null,
                      teamId: null,
                      startDateLocal: `${startDateStr}T00:00:00`,
                      endDateLocal: `${endDateStr}T23:59:59`,
                      page: 0
                    }));
                  }}
                >
                  <span>Đặt lại</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      {/* Danh sách hoạt động */}
      <div className="activity-card">
        <div className="card-header">
          <h2 className="activity-title">
            <FaRunning className="activity-icon" /> Danh sách hoạt động
          </h2>
        </div>
        <div className="card-body">
          {renderActivities()}
          {totalPages > 0 && renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default Activities;